import { ODSTextField } from '@telekom-ods/react-ui-kit';
import React, { useCallback, type ForwardedRef } from 'react';

export interface ODSSearchFieldProps {
  id?: string;
  name: string;
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  label: string;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  helperText?: string;
  size?: 'small' | 'large';
  onFilterClick?: () => void;
  advancedSearch?: boolean;
  multiline?: boolean;
  inputRef?: ForwardedRef<HTMLInputElement>;
}

export const ODSSearchField: React.FC<ODSSearchFieldProps> = ({
  id = '',
  name,
  value = '',
  onChange,
  onBlur,
  label,
  placeholder = '',
  disabled = false,
  helperText = '',
  size = 'small',
  onFilterClick,
  advancedSearch = true,
  multiline = false,
  inputRef = null,
}) => {
  const handleFilterClick = useCallback(() => {
    onFilterClick?.();
  }, [onFilterClick]);

  const ref = React.useRef<HTMLInputElement | null>(null);

  const setInputRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      ref.current = node;
      if (inputRef && node) {
        if (typeof inputRef === 'function') {
          inputRef(node);
        } else {
          inputRef.current = node;
        }
      }
    },
    [inputRef]
  );

  return (
    <ODSTextField
      inputProps={{
        id: id || name,
        type: 'search',
        name: name,
        onChange,
        onBlur,
        ref: setInputRef,
      }}
      placeholderText={placeholder}
      {...(onChange ? { value } : { defaultValue: value })}
      labelText={label}
      disabled={disabled}
      mode={helperText ? 'error' : 'informative'}
      supportMessageProps={{
        helperText: helperText,
      }}
      size={size}
      {...(multiline && { multiline: true })}
      {...(advancedSearch && {
        textFieldIconProps: {
          icon: 'filter-type-standard',
          type: 'buttonContainer' as const,
          buttonProps: {
            label: 'Filter',
            buttonIcon: 'filter-type-standard',
            'data-testid': 'filter-button',
          },
          onClick: handleFilterClick,
        },
      })}
      showRightIcon={advancedSearch}
    />
  );
};

export default ODSSearchField;
